import { Link } from 'react-router-dom';
import { Leaf, Recycle, Heart, Award, ExternalLink, ArrowRight, Check, Scissors, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BOOKING_URL_HAIR = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const BOOKING_URL_BEAUTY = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const impactStats = [
  { number: '95%', label: 'Waste Rescued', description: 'of salon resources diverted from landfill' },
  { number: '100%', label: 'Your Impact', description: 'every service contributes to the environment' },
  { number: '1978', label: 'Since', description: 'caring for Camberwell & the planet' },
];

const whatWeRecycle = [
  { name: 'Your Hair Clippings', description: 'Collected and used to create booms for oil spill clean-ups and turned into nutrient-rich compost', icon: '💇' },
  { name: 'Colour Tubes & Foils', description: 'All metals from your colour service are recycled into new products', icon: '✨' },
  { name: 'Product Containers', description: 'Plastics and bottles from treatments are kept in circulation', icon: '♻️' },
  { name: 'Leftover Chemicals', description: 'Safely processed instead of going down the drain', icon: '🧪' },
  { name: 'Magazines & Paper', description: 'Every page you flipped through gets responsibly recycled', icon: '📄' },
  { name: 'Glass Bottles', description: 'All glass containers are properly recycled', icon: '🫙' },
];

const yourImpact = [
  'Every haircut helps clean up oil spills',
  'Every colour service recycles precious metals',
  'Every treatment supports people with disabilities through Sustainable Salons\' employment program',
  'Every visit reduces landfill waste by 95%',
  'Every ponytail donation creates medical wigs for those in need',
];

export default function Sustainability() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="sustainability-page">
      <SEO page="sustainability" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32" data-testid="sustainability-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1920&h=800&fit=crop"
            alt="Sustainability"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#4A5D44] text-white rounded-full px-4 py-2 mb-6">
              <Leaf className="w-4 h-4" />
              <span className="text-sm font-medium">Your Visit Makes a Difference</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              When You Choose Us, You Give Back
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              At Artistry House, we're proud members of Sustainable Salons Australia. This means every service you receive with us directly contributes to environmental and social causes — without any extra effort from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL_HAIR} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-8 py-4 text-lg">
                  <Scissors className="w-5 h-5 mr-2" />
                  Book Hair & Give Back
                </Button>
              </a>
              <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#4A5D44] text-[#4A5D44] hover:bg-[#4A5D44] hover:text-white rounded-full px-8 py-4 text-lg">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Book Beauty & Give Back
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-16 bg-[#4A5D44]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {impactStats.map((stat) => (
              <div key={stat.label}>
                <p className="text-5xl font-medium text-white mb-2">{stat.number}</p>
                <p className="text-lg text-white/90 font-medium mb-1">{stat.label}</p>
                <p className="text-sm text-white/70">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-24 md:py-32" data-testid="our-commitment">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">Our Commitment</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                We Pay, So You Can Give Back
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                Artistry House is a proud paying member of <strong>Sustainable Salons Australia</strong> — the country's leading salon recycling program and social enterprise.
              </p>
              <p className="text-[#5D4A3A] leading-relaxed mb-6">
                Our membership means that <strong>every service you book with us</strong> automatically contributes to environmental sustainability and community support. You don't need to do anything extra — simply by choosing Artistry House, you're making a positive impact.
              </p>
              <p className="text-[#5D4A3A] leading-relaxed mb-8">
                We handle the sorting, collection, and processing of all salon waste. <strong>95% of everything from your appointment</strong> — from hair clippings to foils to product containers — is rescued from landfill and given a second life.
              </p>
              <div className="bg-[#E8DED2]/50 p-6 rounded-lg border-l-4 border-[#4A5D44]">
                <p className="text-[#2C2420] font-medium mb-2">What this means for you:</p>
                <p className="text-[#5D4A3A]">When you pay for a service at Artistry House, a portion goes toward our Sustainable Salons membership. <strong>You're automatically giving back</strong> to the environment and supporting meaningful social causes.</p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=600&h=500&fit=crop"
                alt="Recycling at salon"
                className="w-full rounded-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="w-8 h-8 text-[#4A5D44]" />
                </div>
                <p className="font-medium text-[#2C2420]">Certified Member</p>
                <p className="text-sm text-[#5D4A3A]">Sustainable Salons Australia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Gets Recycled From Your Visit */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/50" data-testid="what-we-recycle">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">From Your Visit</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">What Gets Recycled</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Every material from your appointment at Artistry House is carefully sorted and sent for proper recycling or repurposing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatWeRecycle.map((item) => (
              <div 
                key={item.name}
                className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-medium text-[#2C2420] mb-2">{item.name}</h3>
                <p className="text-sm text-[#5D4A3A]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Impact Section */}
      <section className="py-24 md:py-32 bg-[#4A5D44]" data-testid="your-impact">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-white/70 mb-4">The Ripple Effect</p>
              <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
                Your Appointment, Your Impact
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-8">
                When you choose Artistry House, you're not just getting a service — you're becoming part of a movement. Here's what your visit helps achieve:
              </p>
              <ul className="space-y-4 mb-8">
                {yourImpact.map((impact) => (
                  <li key={impact} className="flex items-start gap-3 text-white">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    {impact}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 rounded-lg p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Recycle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-medium text-white">Zero Extra Effort</h3>
                  <p className="text-white/70">Just book your appointment</p>
                </div>
              </div>
              <p className="text-white/90 leading-relaxed mb-6">
                "We believe sustainability shouldn't be complicated. That's why we've made it automatic — our membership with Sustainable Salons means your visit always gives back, whether you're getting a haircut, facial, or massage."
              </p>
              <p className="text-white/70 italic">— The Artistry House Team</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ponytail Donation */}
      <section className="py-24 md:py-32" data-testid="ponytail-donation">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/30 rounded-full px-4 py-2 mb-6">
                <Heart className="w-4 h-4 text-[#8D7B68]" />
                <span className="text-sm text-[#5D4A3A] font-medium">Donate at Artistry House</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Ponytail Donations Welcome
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                Thinking about a big chop? Artistry House is a proud collection point for ponytail donations through Sustainable Salons — Australia's biggest donor of ponytails for medical wigs.
              </p>
              <p className="text-[#5D4A3A] leading-relaxed mb-8">
                Your donated hair could become a life-changing wig for someone experiencing hair loss due to illness. It's a beautiful way to give while getting a fresh new look.
              </p>
              <h4 className="font-medium text-[#2C2420] mb-4">Donation Requirements:</h4>
              <ul className="space-y-3 mb-8">
                {['Minimum 35cm in length', 'Hair must be clean and dry', 'No permanent colour or chemical treatments', 'We\'ll tie it securely before cutting'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#5D4A3A]">
                    <div className="w-6 h-6 rounded-full bg-[#4A5D44]/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#4A5D44]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={BOOKING_URL_HAIR} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-6">
                    Book Your Donation Cut
                  </Button>
                </a>
                <a href="https://sustainablesalons.org/recycling/ponytails/" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-[#8D7B68] text-[#8D7B68] hover:bg-[#8D7B68] hover:text-white rounded-full px-6">
                    Learn More
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_d95e4940-e0b0-4d59-9b4c-c960d9d6604f/artifacts/h5uq0jm3_ponty%20taail%201.png"
                alt="Ponytail donation at Artistry House"
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Sustainable Salons */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/50" data-testid="about-sustainable-salons">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">Our Partner</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              About Sustainable Salons
            </h2>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Sustainable Salons is Australia and New Zealand's leading salon recycling program and social enterprise. With over 1,500 member salons, they've diverted more than 2.2 million kilograms of waste from landfill since 2015.
            </p>
            <p className="text-[#5D4A3A] leading-relaxed mb-8">
              The program also employs people with disabilities, creates medical wigs from donated ponytails, and supports various charitable initiatives. By choosing Artistry House, you're supporting all of these incredible causes.
            </p>
            <a 
              href="https://sustainablesalons.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#4A5D44] font-medium hover:gap-3 transition-all"
            >
              Visit Sustainable Salons Website
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <div className="inline-flex items-center gap-2 bg-[#4A5D44]/10 rounded-full px-4 py-2 mb-6">
            <Leaf className="w-4 h-4 text-[#4A5D44]" />
            <span className="text-sm text-[#4A5D44] font-medium">Make a Difference Today</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
            Book & Give Back
          </h2>
          <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto mb-8">
            Every appointment at Artistry House contributes to a greener, kinder future. Book now and be part of the change — no extra effort required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BOOKING_URL_HAIR} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg">
                <Scissors className="w-5 h-5 mr-2" />
                Book Hair
              </Button>
            </a>
            <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-10 py-6 text-lg">
                <Sparkles className="w-5 h-5 mr-2" />
                Book Beauty & Wellness
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
